import type {DiffEntry} from '@github-ui/diff-lines/types'
import type {PullRequestFileTreeDiff} from '../page-data/payloads/file-tree'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {buildProgressiveDiffEntries} from '../utils/build-progressive-diff-entries'

import {identifyDiffEntriesToLoad} from '../utils/identify-diff-entries-to-load'
import type {ProgressiveDiffEntry} from '../types/progressive-diff-types'
import {useFetchDiffEntries} from '../page-data/loaders/use-diff-entries'

export type ProgressiveDiffManager = {
  progressiveDiffEntries: ProgressiveDiffEntry[]
  loadMore: (options: {startingAt: ProgressiveDiffEntry; signal?: AbortSignal}) => Promise<void>
}

// TODO: remove everything related to "paths" in this function after we refactor diff entry fetching to accept
// `pathDigests` param instead of `paths` param. In general, diff entries should be identified and fetched by
// pathDigest exclusively, not path.
export function useProgressiveDiffManager({
  basePath,
  diffSummaries,
  initialDiffEntries,
  selectedPathDigest,
}: {
  basePath: string
  diffSummaries: PullRequestFileTreeDiff[]
  initialDiffEntries: DiffEntry[]
  selectedPathDigest?: string
}): ProgressiveDiffManager {
  const {mutateAsync: asyncFetchMoreDiffEntries} = useFetchDiffEntries(basePath)

  const loadedPathDigests = useRef<Set<string>>(new Set([]))
  const loadingPathDigests = useRef<Set<string>>(new Set([]))
  const loadingPaths = useRef<Set<string>>(new Set())
  const entries = useRef<ProgressiveDiffEntry[]>([])
  const selectedPathIsLoading = useRef(false)
  const [, setForceRender] = useState<ProgressiveDiffEntry[]>([])

  useEffect(() => {
    // Set initial loaded path digests "state"
    const initialLoadedPathDigests = initialDiffEntries.map(entry => entry.pathDigest)
    loadedPathDigests.current = new Set(initialLoadedPathDigests)

    // Set initial progressive diff entries "state"
    entries.current = buildProgressiveDiffEntries({
      diffSummaries,
      loadedPathDigests: loadedPathDigests.current,
      loadingPathDigests: loadingPathDigests.current,
      selectedPathDigest,
    })

    // Ensure entries are rendered if user clears selected path digest
    setForceRender(entries.current)

    // eslint-disable-next-line react-hooks/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // TODO ensure that `selectedPathDigest` isn't an empty string the first time `useProgressiveDiffManager` is mounted.
  // Otherwise we will sometimes (*often) fetch a second batch of entries prematurely.
  const allowedToLoadMore: boolean = useMemo(() => {
    if (!selectedPathDigest) return true
    return loadedPathDigests.current.has(selectedPathDigest)
  }, [selectedPathDigest])

  // Function for loading more diff entries
  const loadMore = useCallback(
    async ({startingAt, signal}: {startingAt: ProgressiveDiffEntry; signal?: AbortSignal}) => {
      const nextPathsToFetch: Set<string> = new Set([])
      const nextPathDigestsToFetch: Set<string> = new Set([])

      // Make sure entries' render modes are up to date before we start loading more
      entries.current = buildProgressiveDiffEntries({
        diffSummaries,
        loadedPathDigests: loadedPathDigests.current,
        loadingPathDigests: loadingPathDigests.current,
        selectedPathDigest,
      })

      // Determine entries to load next
      if (!allowedToLoadMore && !selectedPathIsLoading.current === true) {
        selectedPathIsLoading.current = true
        const entryToLoad = entries.current.find(entry => entry.pathDigest === selectedPathDigest)

        if (entryToLoad) {
          nextPathsToFetch.add(entryToLoad.path)
          nextPathDigestsToFetch.add(entryToLoad.pathDigest)
          loadingPaths.current.add(entryToLoad.path)
        }
      } else {
        const {pathsToLoad: nextPathsToLoad, pathDigestsToLoad: nextPathDigestsToLoad} = identifyDiffEntriesToLoad({
          progressiveDiffEntries: entries.current,
          startingAt,
        })

        for (const path of nextPathsToLoad) {
          nextPathsToFetch.add(path)
          loadingPaths.current.add(path)
        }

        for (const pathDigest of nextPathDigestsToLoad) {
          nextPathDigestsToFetch.add(pathDigest)
          loadingPathDigests.current.add(pathDigest)
        }
      }

      // If there's nothing to fetch, return early
      if (loadingPaths.current.size === 0) {
        return
      }

      // Update entries' render modes based on what we're about to load
      entries.current = buildProgressiveDiffEntries({
        diffSummaries,
        loadedPathDigests: loadedPathDigests.current,
        loadingPathDigests: loadingPathDigests.current,
        selectedPathDigest,
      })

      // Fetch more diff entries
      const newlyLoadedDiffEntries = await asyncFetchMoreDiffEntries({
        paths: Array.from(loadingPaths.current),
        signal,
      })

      // If we didn't get any new diff entries, return early
      if (!newlyLoadedDiffEntries || newlyLoadedDiffEntries.length === 0) {
        return
      }

      // Update loading and loaded states
      for (const path of nextPathsToFetch) {
        loadingPaths.current.delete(path)
      }

      for (const pathDigest of nextPathDigestsToFetch) {
        loadingPathDigests.current.delete(pathDigest)
      }

      for (const entry of newlyLoadedDiffEntries ?? []) {
        loadingPaths.current.delete(entry.path)
        loadingPathDigests.current.delete(entry.pathDigest)
        loadedPathDigests.current.add(entry.pathDigest)
      }

      // Update entries' render modes based on what we just loaded
      entries.current = buildProgressiveDiffEntries({
        diffSummaries,
        loadedPathDigests: loadedPathDigests.current,
        loadingPathDigests: loadingPathDigests.current,
        selectedPathDigest,
      })
    },
    [allowedToLoadMore, asyncFetchMoreDiffEntries, diffSummaries, selectedPathIsLoading, entries, selectedPathDigest],
  )

  return {progressiveDiffEntries: entries.current, loadMore}
}
